/**
 * Reusable Indicator Configurator Component
 * Used in both PromptEditor and IndicatorSet creation
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Switch,
  Stack,
  Divider,
  Alert,
} from '@mui/material';

interface IndicatorSchema {
  display_name?: string;
  category?: string;
  params_schema?: Record<string, any>;
  outputs?: string[];
}

interface IndicatorConfig {
  indicator_name: string;
  params: Record<string, any>;
}

interface IndicatorConfiguratorProps {
  availableIndicators: Record<string, IndicatorSchema>;
  value?: IndicatorConfig | null;
  onChange: (indicator: IndicatorConfig) => void;
  onIndicatorKeyChange?: (key: string) => void;
  showOutputInfo?: boolean;
}

export const IndicatorConfigurator: React.FC<IndicatorConfiguratorProps> = ({
  availableIndicators,
  value,
  onChange,
  onIndicatorKeyChange,
  showOutputInfo = true,
}) => {
  const [selectedIndicatorKey, setSelectedIndicatorKey] = useState<string>('');
  const [indicatorParams, setIndicatorParams] = useState<Record<string, any>>({});

  // Track whether we're syncing from parent to avoid triggering onChange back
  const isSyncingFromParent = useRef(false);

  // Initialize from value prop
  useEffect(() => {
    if (value) {
      const key = Object.keys(availableIndicators).find(
        k => availableIndicators[k].display_name === value.indicator_name || k === value.indicator_name
      );
      if (key && (key !== selectedIndicatorKey || JSON.stringify(value.params || {}) !== JSON.stringify(indicatorParams))) {
        isSyncingFromParent.current = true;
        setSelectedIndicatorKey(key);
        setIndicatorParams(value.params || {});
      }
    }
  }, [value, availableIndicators]);

  const handleIndicatorChange = (key: string) => {
    setSelectedIndicatorKey(key);
    
    // Initialize params with defaults from schema
    const schema = availableIndicators[key];
    const defaultParams: Record<string, any> = {};
    
    if (schema?.params_schema) {
      Object.entries(schema.params_schema).forEach(([paramName, paramSchema]: [string, any]) => {
        if (paramSchema.default !== undefined) {
          defaultParams[paramName] = paramSchema.default;
        }
      });
    }
    
    setIndicatorParams(defaultParams);
    
    // Notify parent
    const indicator = availableIndicators[key];
    onChange({
      indicator_name: indicator.display_name || key,
      params: defaultParams,
    });
    
    if (onIndicatorKeyChange) {
      onIndicatorKeyChange(key);
    }
  };

  // Helper to update a param and notify parent
  const handleParamChange = (paramName: string, paramValue: any) => {
    const newParams = { ...indicatorParams, [paramName]: paramValue };
    setIndicatorParams(newParams);
    if (selectedIndicatorKey) {
      const indicator = availableIndicators[selectedIndicatorKey];
      onChange({
        indicator_name: indicator.display_name || selectedIndicatorKey,
        params: newParams,
      });
    }
  };

  return (
    <Stack spacing={2}>
      {/* Indicator Selection */}
      <FormControl fullWidth>
        <InputLabel>Select Indicator *</InputLabel>
        <Select
          value={selectedIndicatorKey}
          onChange={(e) => handleIndicatorChange(e.target.value)}
          label="Select Indicator *"
        >
          {Object.entries(availableIndicators)
            .sort(([, a], [, b]) => {
              const catA = (a as any).category || 'Other';
              const catB = (b as any).category || 'Other';
              if (catA !== catB) return catA.localeCompare(catB);
              return ((a as any).display_name || '').localeCompare((b as any).display_name || '');
            })
            .map(([key, indicator]: [string, any]) => (
              <MenuItem key={key} value={key}>
                <Box>
                  <Typography variant="body2">
                    {indicator.display_name || key}
                  </Typography>
                  {indicator.category && (
                    <Typography variant="caption" color="text.secondary">
                      {indicator.category}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Dynamic Parameter Fields */}
      {selectedIndicatorKey && availableIndicators[selectedIndicatorKey]?.params_schema && (
        <>
          <Divider>
            <Typography variant="caption" color="text.secondary">
              Parameters
            </Typography>
          </Divider>
          
          {Object.entries(availableIndicators[selectedIndicatorKey].params_schema).map(
            ([paramName, paramSchema]: [string, any]) => {
              const paramType = paramSchema.type;
              const description = paramSchema.description || paramName;
              const defaultValue = paramSchema.default;
              const currentValue = indicatorParams[paramName] ?? defaultValue;

              // Boolean parameters
              if (paramType === 'bool' || paramType === 'boolean') {
                return (
                  <FormControl key={paramName} fullWidth>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{description}</Typography>
                      <Switch
                        checked={!!currentValue}
                        onChange={(e) => {
                          handleParamChange(paramName, e.target.checked);
                        }}
                      />
                    </Stack>
                  </FormControl>
                );
              }

              // Numeric parameters
              if (paramType === 'int' || paramType === 'integer' || paramType === 'float' || paramType === 'number') {
                return (
                  <TextField
                    key={paramName}
                    fullWidth
                    type="number"
                    label={description}
                    value={currentValue ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleParamChange(
                        paramName,
                        paramType === 'int' || paramType === 'integer'
                          ? parseInt(val) || 0
                          : parseFloat(val) || 0
                      );
                    }}
                    helperText={defaultValue !== undefined ? `Default: ${defaultValue}` : undefined}
                    inputProps={{
                      step: paramType === 'int' || paramType === 'integer' ? 1 : 0.01
                    }}
                  />
                );
              }

              // String parameters
              return (
                <TextField
                  key={paramName}
                  fullWidth
                  label={description}
                  value={currentValue ?? ''}
                  onChange={(e) => {
                    handleParamChange(paramName, e.target.value);
                  }}
                  helperText={defaultValue !== undefined ? `Default: ${defaultValue}` : undefined}
                />
              );
            }
          )}
        </>
      )}

      {/* Show indicator info */}
      {showOutputInfo && selectedIndicatorKey && availableIndicators[selectedIndicatorKey] && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="caption">
            <strong>Outputs:</strong>{' '}
            {availableIndicators[selectedIndicatorKey].outputs?.join(', ') || 'N/A'}
          </Typography>
        </Alert>
      )}
    </Stack>
  );
};
